import { useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { useIsMobile } from '@/hooks/use-mobile';
import { usePhoneFullScreenStyle } from '@/hooks/use-visual-viewport';
import { cn } from '@/lib/utils';
import { isInChatPanel, useChatCanCoverPage, useChatDockedBeside, useChatOverPage } from '@/pages/chat/chat-panel-state';
import { openedFromParent } from './subtask-return';
import { TaskStepArrows } from './TaskStepper';
import { TASK_STEP_ATTR, type TaskSteps } from './task-steps';

/**
 * The Board's task pop-up, on a project's Board and the master board (T-26): pass it the
 * TaskDetailView with `layout="wide"`. Desktop (md up): a two-pane pop-up the full height less a sliver (SF-T-305), up to 1800px wide,
 * where the panes scroll, not the pop-up. Phones: the whole screen above the keyboard (T-35), with
 * the task's top row pinned (T-49). With the chat panel docked beside the page (T-78) it sits in the
 * part of the window left of the panel and doesn't block it: clicking or typing in the chat leaves the
 * task open. A sub-task opened from its parent's list (SF-T-134) closes back to the parent. With the chat
 * panel over the page (below 1024px, SF-T-141) the task steps aside, kept as it is, and shows again when the
 * chat closes; for admins below 1024px it's never modal, so opening the chat doesn't remount it.
 * The link dialogs (SF-T-160) use it too, for a factory page or a web page opened from a link in the
 * chat or a task's comments, so they sit beside the chat panel in the same way.
 * With `steps` (SF-T-236, the Inbox), desktop has Previous and Next arrows either side of it, in a wider
 * margin. Phones have none (SF-T-274: they pushed Close off the task's top bar).
 */
export function TaskPopup({
  open,
  onClose,
  title = 'Task',
  children,
  steps,
}: {
  open: boolean;
  onClose: () => void;
  /** For screen readers: what the pop-up shows (the link dialogs, SF-T-160, show pages and web pages in it). */
  title?: string;
  children: ReactNode;
  /** The tasks either side of this one, for the arrows beside the pop-up (desktop). */
  steps?: TaskSteps;
}) {
  const phoneFullScreen = usePhoneFullScreenStyle(open);
  const besideChat = useChatDockedBeside();
  const underChat = useChatOverPage();
  // Non-modal wherever the chat can come over the page (admins, below 1024px), whether it's open or not:
  // flipping `modal` as it opens would remount the task and lose what's being written in it.
  const canBeUnderChat = useChatCanCoverPage();
  // Kept as it was when the task opened, so resizing across 1024px doesn't remount it either; the chat
  // actually coming over it still makes it non-modal (the chat must be usable).
  const [opened, setOpened] = useState({ open, coverable: canBeUnderChat });
  if (opened.open !== open) setOpened({ open, coverable: canBeUnderChat });
  const nonModalForChat = opened.coverable || underChat;
  const location = useLocation();
  const navigate = useNavigate();
  // The entry behind a sub-task opened from its parent is the parent: Back reopens it.
  const close = () => (openedFromParent(location.state) ? navigate(-1) : onClose());
  const isMobile = useIsMobile();
  const arrows = steps && !isMobile && !underChat;
  const keepForChat = (event: { target: EventTarget | null; preventDefault: () => void }) => {
    if (besideChat && isInChatPanel(event.target)) event.preventDefault();
    // The arrows sit outside the pop-up: a click on one moves on, it doesn't close it.
    else if (event.target instanceof Element && event.target.closest(`[${TASK_STEP_ATTR}]`)) event.preventDefault();
  };
  return (
    // Under the chat panel the task stays open whatever closes it would (a click or Escape in the chat),
    // without marking the event handled: the chat's own Escape still closes the chat.
    <Dialog open={open} onOpenChange={(next) => !next && !underChat && close()} modal={!besideChat && !nonModalForChat}>
      <DialogContent
        onInteractOutside={keepForChat}
        onEscapeKeyDown={(event) => keepForChat({ target: document.activeElement, preventDefault: () => event.preventDefault() })}
        // overflow-clip, not hidden: a hidden box can still be scrolled by focus, which slid the
        // whole task up out of view when an answer lower down was picked (T-24). Phones: once the
        // task is showing, its pinned top bar has the close button (T-49), so this one hides; while
        // it loads or fails to, this one stays.
        className={cn(
          'max-h-[calc(100dvh-1rem)] sm:max-w-[min(1800px,calc(100%-4rem))] md:flex md:h-[calc(100dvh-1rem)] md:flex-col md:overflow-clip md:p-0 max-sm:inset-0 max-sm:h-dvh max-sm:max-h-dvh max-sm:max-w-none max-sm:translate-x-0 max-sm:translate-y-0 max-sm:rounded-none max-sm:border-0 max-sm:p-4 [&:has([data-pinned-bar])>[data-slot=dialog-close]]:hidden',
          besideChat && 'left-[calc((100%-var(--chat-dock-width,0px))/2)] shadow-2xl sm:max-w-[min(1800px,calc(100%-var(--chat-dock-width,0px)-4rem))]',
          // Room for the arrows either side.
          arrows && (besideChat ? 'md:max-w-[min(1800px,calc(100%-var(--chat-dock-width,0px)-8rem))]' : 'md:max-w-[min(1800px,calc(100%-8rem))]'),
          underChat && 'hidden!'
        )}
        // Phones: kept to the part of the screen above the keyboard, so the bottom can be scrolled to.
        style={phoneFullScreen}
        // Nothing is focused on open, so a phone's keyboard stays down.
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <DialogTitle className="sr-only">{title}</DialogTitle>
        {/* min-w-0: in the pop-up's grid (phones), a long chip must not widen the task past the screen. */}
        {children ? <div className="min-w-0 md:min-h-0 md:flex-1">{children}</div> : null}
        {/* Drawn outside the pop-up's box (which clips), but from inside it: they sit over its overlay
            and under a dialog opened over the task, and a click on one isn't a click outside. */}
        {arrows ? createPortal(<TaskStepArrows steps={steps} rightInset={besideChat ? 'var(--chat-dock-width,0px)' : undefined} />, document.body) : null}
      </DialogContent>
    </Dialog>
  );
}
